import React, { useState } from "react";
import {
  Button,
  Collapse,
  Form,
  Input,
  Modal,
  notification,
  Typography,
} from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import AnagraficaPaziente from "../components/case-management/AnagraficaPaziente";
import NotizieCliniche from "../components/case-management/NotizieCliniche";
import Documenti from "../components/case-management/Documenti";
import Note from "../components/case-management/Note";
import { useAuth } from "../contexts/authContext";
import { PatientsDTO } from "../models/Patients";
import { patientsService } from "../services/patientsService";
import { casesService } from "../services/casesService";
import { CasesDTO } from "../models/Cases";
import { useLoaderProvider } from "../contexts/LoaderContext";
import { routes } from "../routes";
import { useNavigate } from "react-router-dom";
import TitleTooltip from "../components/layout/TitleTooltip";
import styles from "./CaseManagement.module.css";

const { Panel } = Collapse;
const { Title } = Typography;

const CaseManagement: React.FC = () => {
  const { showLoader, hideLoader } = useLoaderProvider();
  const [patient, setPatient] = useState<PatientsDTO | null>(null);
  const [caseId, setCaseId] = useState<number | null>(null);
  const [caseSaved, setCaseSaved] = useState<CasesDTO | null>(null);
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const [fiscalCode, setFiscalCode] = useState<string>("");
  const { loggedDoctor } = useAuth();
  const navigate = useNavigate();

  const handlePatientCreated = (newPatient: PatientsDTO, newCaseId: number) => {
    setPatient(newPatient);
    setCaseId(newCaseId);
    setCompletedSections((prev) => [...prev, "anagrafica"]);
  };

  const handlePatientUpdated = (updatedPatientId: number) => {
    setCompletedSections((prev) => [...prev, "anagrafica"]);
  };

  const handlePatientPrivacyChange = (privacyConsent: boolean) => {
    if (!privacyConsent && patient) patient.privacyUploadDate = undefined;

    if (privacyConsent && patient)
      patient.privacyUploadDate = new Date().toISOString();

    setPatient(patient);
  };

  const handleSectionComplete = (section: string) => {
    if (!completedSections.includes(section)) {
      setCompletedSections((prev) => [...prev, section]);
    }
  };

  const handleCaseSaved = (newCase: CasesDTO) => {
    setCaseSaved(newCase);
  };

  const isCompleted = (section: string) => completedSections.includes(section);
  const isConfirmButtonDisabled = Boolean(
    !caseId || (caseSaved && caseSaved.openingDate)
  );

  const renderPanelHeader = (
    title: string,
    descriptionTooltip: string,
    section: string
  ) => (
    <div className={styles.panelHeader}>
      <div className={styles.titleContainer}>
        <TitleTooltip
          title={title}
          tooltipText={descriptionTooltip}
          titleLevel={4}
          tooltipColor="#ff4d4f"
        />
      </div>
      {isCompleted(section) && (
        <CheckCircleFilled className={styles.checkIcon} />
      )}
    </div>
  );

  const onSearchPatient = async () => {
    try {
      showLoader("Ricerca del paziente...");
      const patientData = await patientsService.getAllPatients({
        "taxIdCode.equals": fiscalCode,
      });
      if (patientData.length > 0) {
        setPatient(patientData[0]);
        setFiscalCode("");
      } else {
        notification.error({ message: "Paziente non trovato" });
      }
    } catch (error) {
      console.error("Errore durante la ricerca del paziente:", error);
    } finally {
      hideLoader();
    }
  };

  const onConfirmCase = async () => {
    if (!patient) return;

    const patientRefetched = await patientsService.getPatientById(patient.id!);
    if (!patientRefetched?.privacyUploadDate) {
      // mostrare errore se la privacy non è stata caricata
      Modal.error({
        title: "Consenso privacy non caricato",
        content: "Vai alla sezione documenti per caricare il consenso privacy",
      });
      return;
    }

    try {
      showLoader("Apertura del caso...");
      await casesService.changeStatus(caseId!, "OPEN");
      notification.success({ message: "Caso aperto con successo" });
      navigate(`${routes.DASHBOARD}`);
    } catch (error) {
      console.error("Errore durante la conferma del caso:", error);
    } finally {
      hideLoader();
    }
  };

  return (
    <div className={styles.container}>
      <Title level={2}>Gestione Caso</Title>
      <Form layout="inline" className={styles.searchForm}>
        <Form.Item label="Codice Fiscale">
          <Input
            value={fiscalCode}
            onChange={(e) => setFiscalCode(e.target.value)}
            placeholder="Inserisci codice fiscale"
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            onClick={onSearchPatient}
            disabled={!fiscalCode}
          >
            Cerca Paziente
          </Button>
        </Form.Item>
      </Form>
      <Collapse defaultActiveKey={["1"]}>
        <Panel
          header={renderPanelHeader(
            "Anagrafica Paziente",
            "Sezione dedicata alla creazione dell'anagrafica del pazinete",
            "anagrafica"
          )}
          key="1"
        >
          <AnagraficaPaziente
            patient={patient}
            isEditable={true}
            onPatientCreated={handlePatientCreated}
            onPatientUpdated={handlePatientUpdated}
            onCaseSaved={handleCaseSaved}
          />
        </Panel>
        <Panel
          header={renderPanelHeader(
            "Documenti",
            "Sezione dedicata all'inserirmento dei documenti legati al caso",
            "documenti"
          )}
          key="2"
          collapsible={!caseId ? "disabled" : "header"}
        >
          {caseId && patient && (
            <Documenti
              patient={patient}
              caseId={caseId}
              onSave={() => handleSectionComplete("documenti")}
              setPatientPrivacy={(privacyConsens) =>
                handlePatientPrivacyChange(privacyConsens)
              }
            />
          )}
        </Panel>
        <Panel
          header={renderPanelHeader(
            "Notizie Cliniche",
            "Sezione dedicata all'inserimento delle notizie cliniche del paziente, anamnesi familiare e patologica",
            "notizieCliniche"
          )}
          key="3"
          collapsible={!caseId ? "disabled" : "header"}
        >
          {caseId && (
            <NotizieCliniche
              isEditable={true}
              caseId={caseId}
              onSectionComplete={() => handleSectionComplete("notizieCliniche")}
            />
          )}
        </Panel>
        <Panel
          header={renderPanelHeader(
            "Note",
            "Sezione dedicata allo scambio di messaggio ed informazioni tra i dottori che collaborano al caso",
            "note"
          )}
          key="4"
          collapsible={!caseId ? "disabled" : "header"}
        >
          {caseSaved?.id && loggedDoctor!.id && <Note caseDetail={caseSaved} />}
        </Panel>
      </Collapse>
      <div style={{ marginTop: "20px", textAlign: "center" }}>
        <Button
          type="primary"
          size="large"
          onClick={onConfirmCase}
          disabled={isConfirmButtonDisabled}
        >
          Conferma Apertura Caso
        </Button>
      </div>
    </div>
  );
};

export default CaseManagement;
